import { CATEGORICAL } from "../../../config/vizPalette";
import { InsightSection, QueryState } from "./InsightSection";
import { useInsightMetrics, useNextPlay } from "./useSteamInsights";

const storeUrl = (appid) => `https://store.steampowered.com/app/${appid}`;

const ReasonTag = ({ reason, colour }) => (
    <li className="flex items-start gap-2 text-sm text-gray-700">
        <span aria-hidden="true" className="mt-1.5 inline-block h-2.5 w-2.5 flex-none rounded-full" style={{ backgroundColor: colour }} />
        <span>
            <span className="font-semibold">{reason.label}:</span> {reason.text}
        </span>
    </li>
);

const GameCard = ({ game, rank, signalColour }) => (
    <li className="flex flex-col overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
        {game.header_image_url && (
            <img src={game.header_image_url} alt="" loading="lazy" className="aspect-[460/215] w-full object-cover" />
        )}
        <div className="flex flex-1 flex-col p-4">
            <div className="flex items-start justify-between gap-3">
                <a
                    href={storeUrl(game.appid)}
                    target="_blank"
                    rel="noreferrer"
                    className="min-h-[44px] text-base font-semibold text-gray-800 underline decoration-gray-300 underline-offset-4 hover:text-gray-900"
                >
                    <span className="text-gray-500">{rank}.</span> {game.name || `App ${game.appid}`}
                </a>
                <span className="flex-none rounded-full bg-gray-100 px-2 py-0.5 font-mono text-sm text-gray-700" title="Recommendation score">
                    {game.score.toFixed(2)}
                </span>
            </div>
            <ul className="mt-2 space-y-1">
                {game.reasons.map((reason) => (
                    <ReasonTag key={reason.signal} reason={reason} colour={signalColour(reason.signal)} />
                ))}
            </ul>
        </div>
    </li>
);

export const NextPlayList = () => {
    const registry = useInsightMetrics();
    const query = useNextPlay(9);
    const signals = registry.data?.signals || [];
    const signalColour = (key) => CATEGORICAL[Math.max(signals.findIndex((s) => s.key === key), 0) % CATEGORICAL.length];

    return (
        <InsightSection
            id="next-play"
            title="What to play next"
            intro="Each game is scored against four signals and the weighted scores are added up, so a game that is nearly finished and has easy achievements left beats one that only ticks one box."
        >
            {signals.length > 0 && (
                <ul className="mb-4 grid grid-cols-1 gap-2 sm:grid-cols-2">
                    {signals.map((signal) => (
                        <li key={signal.key} className="flex items-start gap-2 text-sm text-gray-500">
                            <span
                                aria-hidden="true"
                                className="mt-1.5 inline-block h-2.5 w-2.5 flex-none rounded-full"
                                style={{ backgroundColor: signalColour(signal.key) }}
                            />
                            <span>
                                <span className="font-semibold text-gray-700">{signal.label}</span> (weight {signal.weight}):{" "}
                                {signal.description}
                            </span>
                        </li>
                    ))}
                </ul>
            )}
            <QueryState query={query} isEmpty={!query.data?.length} emptyText="No recommendations yet.">
                <ol className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                    {(query.data || []).map((game, index) => (
                        <GameCard key={game.appid} game={game} rank={index + 1} signalColour={signalColour} />
                    ))}
                </ol>
            </QueryState>
        </InsightSection>
    );
};
